import React, { type FC } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { Constants } from '@mobi/settings'
import { isReactNativeApp as isNative } from '@mobi/web-native-comms/web'
import { useServerConfig } from '@core/Data/ServerConfig/useServerConfig'
import { keys } from '@core/Services/Analytics/keys'
import { trackEvent } from '@core/Services/Analytics/track'
import { fullClientVersion } from '@core/Utils/clientVersion'
import { InternalVersionNumber } from '../InternalVersionNumber'
import { ExternalLink } from './ExternalLink'
import {
  CopyrightStyled as Copyright,
  DownloadAppStyled as DownloadApp,
  FooterStyled,
  GamblingHelplineStyled as GamblingHelpline,
  LogoStyled as Logo,
  RwwaResponsibleWageringStyled as RwwaResponsibleWagering,
  SocialsStyled as Socials,
} from './Footer.styles'
import {
  AppleAppStore as AppleAppStoreLogo,
  GooglePlayStore as GooglePlayStoreLogo,
  TabTouch as TabTouchLogo,
} from './Logos'
import { SetDepositLimitButton } from './SetDepositLimitButton'
import { UnbundlingStatusDot } from './UnbundlingStatusDot'
import { VersionSelector } from './VersionSelector'

type Props = {
  /** Handler for 'set deposit limit' button click events */
  onSetDepositLimitClick: VoidFunction
  /** Handler for copyright click events */
  onCopyrightClick: VoidFunction
}

export const Footer: FC<Props> = ({ onSetDepositLimitClick, onCopyrightClick }) => {
  const [isTrayOpen, setIsTrayOpen] = React.useState(false)
  const serverConfig = useServerConfig()
  const isInternal = serverConfig?.isInternalRequest ?? false

  return (
    <FooterStyled>
      <Logo>
        <TabTouchLogo />
      </Logo>

      <Socials>
        <ExternalLink
          href='https://www.instagram.com/tabtouch'
          onClick={() => trackExitNavigation('instagram')}
          data-tid-home-instagram
        >
          <Icon name='LogoInstagram' />
        </ExternalLink>
        <ExternalLink
          href='https://www.facebook.com/login.php?next=https%3A%2F%2Fwww.facebook.com%2FTABtouch'
          onClick={() => trackExitNavigation('facebook')}
          data-tid-home-facebook
        >
          <Icon name='LogoFacebook' />
        </ExternalLink>
        <ExternalLink
          href='https://twitter.com/tab_touch'
          onClick={() => trackExitNavigation('twitter')}
          data-tid-home-twitter
        >
          <Icon name='LogoX' />
        </ExternalLink>
      </Socials>

      <GamblingHelpline>
        <h1>WHAT ARE YOU REALLY GAMBLING WITH?</h1>

        <p>
          For free and confidential support call{' '}
          <strong>
            <a
              href={`tel:${Constants.GamblingHelpPhoneNumber}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {Constants.GamblingHelpPhoneNumber}
            </a>
          </strong>{' '}
          or visit{' '}
          <strong>
            <ExternalLink
              href={Constants.GamblingHelpOnlineURL}
              onClick={() => trackExitNavigation('gambling-help-online')}
            >
              gamblinghelponline.org.au
            </ExternalLink>
          </strong>
        </p>

        {onSetDepositLimitClick && <SetDepositLimitButton onClick={onSetDepositLimitClick} />}
      </GamblingHelpline>

      <RwwaResponsibleWagering>
        <p>
          BetStop - the National Self-Exclusion Register&trade; is a free service for people who
          want to exclude from all Australian online wagering providers. Visit{' '}
          <ExternalLink href={Constants.BetStopURL} onClick={() => trackExitNavigation('bet-stop')}>
            www.betstop.gov.au
          </ExternalLink>{' '}
          for more information.
        </p>
        <p>
          RWWA supports{' '}
          <ExternalLink
            href={Constants.ResponsibleWageringURL}
            onClick={() => trackExitNavigation('responsible-wagering')}
          >
            Responsible Wagering
          </ExternalLink>
        </p>
        <p>
          RWWA&apos;s gambling operations are governed by its{' '}
          <ExternalLink
            href='https://www.rwwa.com.au/docs/rwwalibraries/default-document-library/rwwa-responsible-wagering-code-of-practice-007-1.pdf'
            onClick={() => trackExitNavigation('responsible-gambling-code-of-practice')}
          >
            Responsible Gambling Code of Practice
          </ExternalLink>{' '}
          and for South Australian residents by the{' '}
          <ExternalLink
            href='https://www.cbs.sa.gov.au/documents/FN_ABO-Gambling-Code-of-Practice.pdf'
            onClick={() => trackExitNavigation('responsible-gambling-code-of-practice-sa')}
          >
            South Australian Responsible Gambling Code of Practice.
          </ExternalLink>
        </p>
      </RwwaResponsibleWagering>

      {!isNative && (
        <DownloadApp>
          <h1>Download our app</h1>
          <span className='links'>
            <ExternalLink
              href='https://itunes.apple.com/au/app/tabtouch/id472906558?mt=8'
              data-tid-home-ios
              onClick={() => trackExitNavigation('apple-app-store')}
            >
              <AppleAppStoreLogo />
            </ExternalLink>
            <ExternalLink
              href='https://play.google.com/store/apps/details?id=com.rwwa.android.tabtouch'
              data-tid-home-android
              onClick={() => trackExitNavigation('android-play-store')}
            >
              <GooglePlayStoreLogo />
            </ExternalLink>
          </span>
        </DownloadApp>
      )}

      <Copyright>
        <p>
          Gamble responsibly. 18+ only.{' '}
          <span onClick={onCopyrightClick}>&copy; Copyright RWWA {new Date().getFullYear()}</span>
          <br />
          {!isInternal && (
            <span>
              <UnbundlingStatusDot /> v{fullClientVersion}
            </span>
          )}
          {isInternal && (
            <InternalVersionNumber
              version={fullClientVersion}
              onClick={() => setIsTrayOpen(true)}
            />
          )}
          <br />
          {isInternal && <Link to='/toolbox'>Toolbox</Link>}
        </p>
      </Copyright>

      {isTrayOpen &&
        createPortal(
          <Tray
            isOpen={true}
            headerTitle='Web App Latest Versions'
            shouldCoverFullscreen={true}
            onDismiss={() => setIsTrayOpen(false)}
          >
            <VersionSelector
              currentVersion={fullClientVersion}
              currentMajorVersion={parseInt(`${VERSION_MAJOR}`)}
            />
          </Tray>,
          document.body
        )}
    </FooterStyled>
  )
}

// =============
// Local Helpers
// =============

function trackExitNavigation(destination: string) {
  trackEvent(keys.navExit, { destination })
}
